export const normalizeConsultationText = (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLocaleLowerCase('pt-BR');
export const consultationDigits = (value) => String(value ?? '').replace(/\D/g, '');
export const consultationDate = (value) => {
  const text = String(value ?? '').trim();
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
  const br = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  const date = iso ? `${iso[1]}-${iso[2]}-${iso[3]}` : br ? `${br[3]}-${br[2]}-${br[1]}` : '';
  if (!date) return '';
  const parsed = new Date(`${date}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === date ? date : '';
};
export const consultationDateInRange = (value, from, to) => {
  if (!from && !to) return true;
  const date = consultationDate(value), start = consultationDate(from), end = consultationDate(to);
  return Boolean(date && (!from || start && date >= start) && (!to || end && date <= end));
};
// Existing personal-record filters accept a year, month or month/year too.
export const consultationDateInFlexibleRange = (value, from, to) => {
  if (!from && !to) return true;
  const date = consultationDate(value);
  if (!date) return false;
  const parse = input => {
    const text = String(input || '').trim();
    if (!text) return null;
    const full = consultationDate(text);
    if (full) return { full };
    if (/^\d{4}$/.test(text)) return { year: Number(text) };
    const monthYear = text.match(/^(\d{1,2})[/-](\d{4})$/);
    const isoMonth = text.match(/^(\d{4})-(\d{1,2})$/);
    const month = monthYear ? Number(monthYear[1]) : isoMonth ? Number(isoMonth[2]) : /^\d{1,2}$/.test(text) ? Number(text) : 0;
    return month >= 1 && month <= 12 ? { month, year: monthYear ? Number(monthYear[2]) : isoMonth ? Number(isoMonth[1]) : null } : { invalid: true };
  };
  const start = parse(from), end = parse(to);
  if (start?.invalid || end?.invalid) return false;
  const matches = bound => !bound || (!bound.year || Number(date.slice(0, 4)) === bound.year) && (!bound.month || Number(date.slice(5, 7)) === bound.month);
  return (start?.full ? date >= start.full : matches(start)) && (end?.full ? date <= end.full : matches(end));
};
export const matchesWorkRegistrationFilters = (student = {}, filters = {}) => {
  const record = student.customFields?.worldOfWorkRecord || {};
  return consultationDateInRange(record.enrollmentDate, filters.wrEnrollmentStart, filters.wrEnrollmentEnd)
    && consultationDateInRange(record.exitDate, filters.wrExitStart, filters.wrExitEnd)
    && (!normalizeConsultationText(filters.wrExitReason) || normalizeConsultationText(record.exitReason) === normalizeConsultationText(filters.wrExitReason))
    && normalizeConsultationText(record.observations).includes(normalizeConsultationText(filters.wrObservations));
};
export const matchesConsultationSearch = (student, query) => {
  const text = normalizeConsultationText(query);
  if (!text) return true;
  const values = [student.fullName, student.cpf, student.registrationNumber, student.whatsapp, student.phone, student.email];
  const digits = consultationDigits(query);
  return values.some((value) => normalizeConsultationText(value).includes(text)) ||
    (digits.length >= 3 && [student.cpf, student.whatsapp, student.phone, student.registrationNumber].some((value) => consultationDigits(value).includes(digits)));
};
const rank = (row) => ({ ACTIVE: 0, PLANNED: 1, ENDED: 2 }[row.employmentStatus || row.status] ?? 3);
export const consultationEmploymentByStudent = (rows = []) => {
  const result = {};
  [...rows].sort((a, b) => rank(a) - rank(b) || consultationDate(b.startDate || b.admissionDate).localeCompare(consultationDate(a.startDate || a.admissionDate)) || Number(b.employmentId || b.id || 0) - Number(a.employmentId || a.id || 0)).forEach((item) => {
    const key = String(item.studentId || '').trim();
    if (!key || result[key]) return;
    result[key] = {
      apprenticeNumber: item.apprenticeNumber || item.benefits?.apprenticeNumber || '',
      registrationCreatedAt: item.employmentCreatedAt || item.createdAt || '',
      currentCompanyName: item.companyName || item.company?.tradeName || item.company?.corporateName || item.companyCorporateName || '',
      companyId: item.companyId || item.company?.id || '',
      role: item.role || '', courseName: item.courseName || '',
      admissionDate: consultationDate(item.admissionDate || item.startDate), startDate: consultationDate(item.startDate || item.admissionDate),
      endDate: item.benefits?.contractEndDateCleared ? '' : consultationDate(item.endDate || item.terminationDate),
      bankName: item.bankName || '', bankAgency: item.bankAgency || '', bankAccount: item.bankAccount || '',
      hoursPerDay: item.hoursPerDay ?? '', workload: item.workload || '', courseTime: item.courseTime || item.classPeriod || '',
    };
  });
  return result;
};
export const matchesWorldOfWorkFilters = (mt = {}, filters = {}) => {
  const equals = (value, filter) => !normalizeConsultationText(filter) || normalizeConsultationText(value) === normalizeConsultationText(filter);
  const hours = String(filters.mtHoursPerDay ?? '').trim();
  return equals(mt.currentCompanyName, filters.mtCompany) && equals(mt.role, filters.mtRole) && equals(mt.courseName, filters.mtCourse)
    && consultationDateInRange(mt.admissionDate, filters.mtAdmissionStart, filters.mtAdmissionEnd)
    && consultationDateInRange(mt.endDate, filters.mtEndStart, filters.mtEndEnd)
    && (!hours || String(mt.hoursPerDay ?? '').trim() !== '' && Number(String(mt.hoursPerDay).replace(',', '.')) === Number(hours.replace(',', '.')));
};
